/**
 * Setup failure classification for the account Studio picker (ENG-4483).
 *
 * WHY a separate module: the picker component and the e2e telemetry probe
 * (`test-probes/studio-link-failed-telemetry-probe.ts`) must classify a refused
 * link the SAME way, otherwise the `studio_link_failed` receipt would prove a
 * mapping the product never runs. Pure functions, no React, no transport.
 *
 * Per rules (ai.knowledge/global): think-in-systems, code-comments-required,
 * reference-rules-in-comments.
 */
import { AgentRegistrySetupReason } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import type { StudioBootstrapErrorType } from '#~/analytics/domains/studio-bootstrap.js';
import { readSetupFailure } from '#~/api/agent/setup-failure.js';
import { ApiError } from '#~/api/types.js';

/** The subset of analytics error types a setup (link / create) action can produce. */
export type SetupErrorType = Extract<
  StudioBootstrapErrorType,
  | 'authentication'
  | 'permission_denied'
  | 'inventory_conflict'
  | 'capacity_exhausted'
  | 'capacity_unknown'
  | 'capability_unavailable'
  | 'network'
  | 'bridge_handshake'
  | 'unknown'
>;

/** HTTP-style status carried by an ApiError or by the protobuf Error in its cause chain. */
export const apiCode = (cause: unknown): number | undefined => {
  if (cause instanceof ApiError) return cause.code;
  if (cause instanceof Error && cause.cause !== undefined) return apiCode(cause.cause);
  if (cause !== null && typeof cause === 'object' && 'code' in cause) {
    const code = Number((cause as { code: unknown }).code);
    return Number.isFinite(code) ? code : undefined;
  }
  return undefined;
};

// ENG-4483 W2/W3: the service names WHY a setup action was refused in the error
// envelope; branch on that before falling back to transport codes so capacity
// and capability refusals are never mistaken for a generic conflict.
const setupReasonErrorType = (cause: unknown): SetupErrorType | undefined => {
  const failure = readSetupFailure(cause);
  switch (failure?.reason) {
    case AgentRegistrySetupReason.AGENT_REGISTRY_SETUP_REASON_CAPACITY_EXHAUSTED:
      return 'capacity_exhausted';
    case AgentRegistrySetupReason.AGENT_REGISTRY_SETUP_REASON_CAPACITY_UNKNOWN:
      return 'capacity_unknown';
    case AgentRegistrySetupReason.AGENT_REGISTRY_SETUP_REASON_CAPABILITY_UNAVAILABLE:
      return 'capability_unavailable';
    case AgentRegistrySetupReason.AGENT_REGISTRY_SETUP_REASON_OCCUPANCY_CHANGED:
      return 'inventory_conflict';
    case AgentRegistrySetupReason.AGENT_REGISTRY_SETUP_REASON_PERMISSION_DENIED:
      return 'permission_denied';
    default:
      return undefined;
  }
};

/**
 * Classify a failed setup action for analytics and UI copy. Reason envelope
 * first, then status code, then a bounded message sniff for transport errors
 * that never reached the API.
 */
export const setupErrorType = (cause: unknown): SetupErrorType => {
  const reasoned = setupReasonErrorType(cause);
  if (reasoned !== undefined) return reasoned;
  const code = apiCode(cause);
  if (code === 401) return 'authentication';
  if (code === 403) return 'permission_denied';
  if (code === 409) return 'inventory_conflict';
  const message = cause instanceof Error ? cause.message.toLowerCase() : '';
  if (message.includes('bridge') || message.includes('link')) return 'bridge_handshake';
  if (message.includes('network') || message.includes('timeout') || message.includes('fetch'))
    return 'network';
  return 'unknown';
};
